import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  NgModule,
} from "@angular/core";
import { formatMessage } from "devextreme/localization";
import {
  DxFileUploaderComponent,
  DxDataGridModule,
  DxFileUploaderModule,
  DxProgressBarModule,
  DxSelectBoxModule,
} from "devextreme-angular";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { CommonModule } from "@angular/common";
import { AlertModule } from "../alert/alert.component";
import { Apollo } from "apollo-angular";
import { FIND_ALL_COMPONENT_PRICE_SCALE_ENTRIES_BY_PUBLISHER } from "../../graphql/calculatorGql";
import { GraphQLModule } from "../../../graphql.module";

export interface ComponentPriceScaleEntry {
  width: number;
  length: number;
  quantity: number;
  component: string;
  part: string;
  color: string;
  finishingType: string;
  specialEffects: string;
  type: string;
  pages: number;
  plates: number;
  mr: number;
  run: number;
  publisherId?: string;
}

export interface ValidationError {
  code: string;
  error: string;
  row?: number;
  column?: string;
  invalidValue?: string;
  validationRule?: string;
}

@Component({
  selector: "app-component-pricelist",
  templateUrl: "./component-pricelist.component.html",
  styleUrls: ["./component-pricelist.component.scss"],
})
export class ComponentPricelistComponent implements OnInit, OnChanges {
  @ViewChild(DxFileUploaderComponent) fileUploader: any;
  @Input() publisherId: string | null;
  @Output() reloadEvent = new EventEmitter<any>();

  formatMessage = formatMessage;

  // Upload states
  isUploading = false;
  isDropZoneActive = false;
  showErrorBorders = false;
  invalidFileMessage = "";
  validationError: ValidationError | null = null;
  successAlertMessage = "";
  progressVisible = false;
  progressValue = 0;
  hasNetworkConnection = true;

  // Data
  componentPriceData: ComponentPriceScaleEntry[] = [];
  uploadEndpointUrl = `${environment.baseUrl}/api/calculator/us/uploadComponentPriceScale`;

  constructor(private http: HttpClient, private apollo: Apollo) {
    this.onDropZoneEnter = this.onDropZoneEnter.bind(this);
    this.onDropZoneLeave = this.onDropZoneLeave.bind(this);
    this.onProgress = this.onProgress.bind(this);
    this.onUploadStarted = this.onUploadStarted.bind(this);
    this.onUploadError = this.onUploadError.bind(this);
    this.onSelectedFileChanged = this.onSelectedFileChanged.bind(this);
  }

  ngOnInit(): void {
    this.hasNetworkConnection = window.navigator.onLine;
    this.loadComponentPriceData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ("publisherId" in changes && changes["publisherId"].currentValue) {
      this.loadComponentPriceData();
    }
  }

  /**
   * Load component price data from backend for the selected publisher
   */
  private loadComponentPriceData(): void {
    if (!this.publisherId) {
      this.componentPriceData = [];
      return;
    }

    this.apollo
      .query<{
        findAllComponentPriceScaleEntriesByPublisher: ComponentPriceScaleEntry[];
      }>({
        query: FIND_ALL_COMPONENT_PRICE_SCALE_ENTRIES_BY_PUBLISHER,
        variables: {
          publisherId: this.publisherId,
        },
        fetchPolicy: "no-cache", // Always fetch fresh data
      })
      .subscribe({
        next: (result) => {
          this.componentPriceData =
            result.data.findAllComponentPriceScaleEntriesByPublisher || [];
          console.log(
            "Component price data loaded:",
            this.componentPriceData.length,
            "entries"
          );
        },
        error: (error) => {
          console.error("Error loading component price data:", error);
          this.componentPriceData = [];
        },
      });
  }

  /**
   * Public method to reload data (can be called from parent component)
   */
  public reloadData(): void {
    this.loadComponentPriceData();
  }

  resetDropZoneErrorValues() {
    this.showErrorBorders = false;
    this.invalidFileMessage = "";
    this.validationError = null;
  }

  onDropZoneEnter(e) {
    if (e.dropZoneElement.id === "component-dropzone-external") {
      this.isDropZoneActive = true;
    }
  }

  onDropZoneLeave(e) {
    this.resetDropZoneErrorValues();
    if (e.dropZoneElement.id === "component-dropzone-external") {
      this.isDropZoneActive = false;
    }
  }

  onDropZoneClick(e) {
    if (!this.hasNetworkConnection) {
      e.preventDefault();
    }
  }

  onSelectedFileChanged() {
    let firstFile = this.fileUploader.instance._files[0];

    if (firstFile) {
      if (!firstFile.isValid()) {
        this.invalidFileMessage = formatMessage(
          "upload.invalid.xlsx",
          firstFile.value.name
        );
        this.isDropZoneActive = false;
        this.showErrorBorders = true;
      } else {
        this.resetDropZoneErrorValues();

        // Validate publisher is selected
        if (!this.publisherId) {
          console.error("No publisher selected for upload");
          this.invalidFileMessage =
            "Please select a publisher before uploading files.";
          this.showErrorBorders = true;
          return;
        }

        const file = this.fileUploader.instance._files[0];
        const formData = new FormData();
        formData.append("file", file.value);
        formData.append("publisherId", this.publisherId);

        if (file) {
          this.uploadCsvFile(formData, file);
        }
      }
    }
  }

  private async uploadCsvFile(formData: FormData, file: any) {
    this.http
      .post<ComponentPriceScaleEntry[]>(this.uploadEndpointUrl, formData)
      .subscribe(
        (data: ComponentPriceScaleEntry[]) => {
          let fileUploaderInstance = <any>this.fileUploader.instance;
          fileUploaderInstance._isProgressStarted = false;
          this.successHandler(file.value);
          this.progressVisible = false;
          this.progressValue = 100;

          // Reload data from GraphQL instead of using upload response
          this.loadComponentPriceData();

          // Emit reload event to parent component
          this.reloadEvent.emit(data);
        },
        (error) => {
          console.error("Upload error:", error);

          // Check if it's a validation error
          if (
            error.status === 400 &&
            error.error &&
            error.error.code === "VALIDATION_ERROR"
          ) {
            this.validationErrorHandler(error.error, file.value);
          } else {
            this.errorHandler(file.value);
          }
        },
        () => {
          // Complete
        }
      );
  }

  onProgress(e) {
    this.progressValue = (e.bytesLoaded / e.bytesTotal) * 100;
  }

  onUploadStarted(e) {
    this.isUploading = true;
    this.progressVisible = true;
    this.successAlertMessage = "";
  }

  onUploadError(e) {
    this.errorHandler(e.file);
  }

  private successHandler(file: any) {
    this.successAlertMessage = formatMessage(
      "publisherConfiguration.componentPricelist.upload.success",
      file.name
    );
    this.isUploading = false;
    this.isDropZoneActive = false;

    // Reset any previous errors
    this.resetDropZoneErrorValues();
  }

  private errorHandler(file: any) {
    this.invalidFileMessage = formatMessage(
      "publisherConfiguration.componentPricelist.upload.error",
      file.name
    );
    this.isUploading = false;
    this.isDropZoneActive = false;
    this.progressVisible = false;
  }

  private validationErrorHandler(validationError: ValidationError, file: any) {
    this.validationError = validationError;
    this.isUploading = false;
    this.isDropZoneActive = false;
    this.progressVisible = false;
    this.showErrorBorders = true;

    // Set a general error message but the detailed validation error will be shown separately
    this.invalidFileMessage = formatMessage(
      "publisherConfiguration.componentPricelist.upload.validationError",
      file.name
    );
  }

  refresh() {
    window.location.reload();
  }
}

@NgModule({
  imports: [
    CommonModule,
    DxDataGridModule,
    DxFileUploaderModule,
    DxProgressBarModule,
    DxSelectBoxModule,
    AlertModule,
    GraphQLModule,
  ],
  declarations: [ComponentPricelistComponent],
  exports: [ComponentPricelistComponent],
})
export class ComponentPricelistModule {}
